'use client';

import * as amplitude from '@amplitude/analytics-browser';
import type {plugin} from '@amplitude/engagement-browser';

const apiKey = process.env.NEXT_PUBLIC_AMPLITUDE_API_KEY;
const analyticsDisabled = process.env.ANALYTICS_DISABLED === 'true';

if (!apiKey) throw new Error('Amplitude API key is not set');

let engagementPluginModule: typeof plugin | null = null;

const amplitudeService = {
  init: async () => {
    if (analyticsDisabled) return;

    amplitude.init(apiKey, undefined, {
      logLevel: amplitude.Types.LogLevel.Warn,
      defaultTracking: {
        sessions: true,
        pageViews: true,
        formInteractions: true,
        fileDownloads: true,
      },
    });

    try {
      const engagementPlugin = await getEngagementPlugin();
      if (engagementPlugin) {
        // Comes from doc https://amplitude.com/docs/guides-and-surveys/sdk
        amplitude.add(engagementPlugin());
      }
    } catch (error) {
      console.error('Error adding engagement plugin:', error);
    }
  },

  logEvent: (
    eventName: string | amplitude.Types.BaseEvent,
    eventProperties = {},
  ) => {
    if (analyticsDisabled) return;
    amplitude.track(eventName, eventProperties);
  },

  setUserId: (userId: string | undefined) => {
    if (analyticsDisabled) return;
    amplitude.setUserId(userId);
  },

  setUserProperties: (
    userProperties:
      | {[s: string]: string | boolean}
      | ArrayLike<string | boolean>,
  ) => {
    if (analyticsDisabled) return;
    const identify = new amplitude.Identify();
    Object.entries(userProperties).forEach(([key, value]) => {
      if (key.startsWith('first_touch_')) {
        identify.setOnce(key, value);
      } else {
        identify.set(key, value);
      }
    });
    amplitude.identify(identify);
  },

  reset: () => {
    if (analyticsDisabled) return;
    amplitude.reset();
  },
};

const getEngagementPlugin = async () => {
  if (typeof window === 'undefined') return null;

  if (!engagementPluginModule) {
    try {
      const engagementSdk = await import('@amplitude/engagement-browser');
      engagementPluginModule = engagementSdk.plugin;
    } catch (error) {
      console.error('Failed to load engagement plugin:', error);
      return null;
    }
  }

  return engagementPluginModule;
};

export default amplitudeService;
